import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, Instagram, Facebook, Music2 } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const social = (key, settings) => settings?.[key] || "";

export const Footer = () => {
  const [settings, setSettings] = useState({});
  const { isEnabled } = useSiteSettings();
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const socials = [
    { icon: Youtube, key: "youtube_channel_url", label: "YouTube" },
    { icon: Instagram, key: "instagram_url", label: "Instagram" },
    { icon: Music2, key: "tiktok_url", label: "TikTok" },
    { icon: Facebook, key: "facebook_url", label: "Facebook" },
  ];

  const cols = [
    { title: "Explore", links: [["Videos", "/videos", "youtube"], ["The Journey", "/journey", "journey"], ["Fish Room", "/fish-room", "fish_room"], ["Breeding", "/breeding", "breeding"], ["Business", "/business", "business"]] },
    { title: "More", links: [["Test Lab", "/test-lab", "test_lab"], ["Community", "/community", "community"], ["Forum", "/forum", "forum"], ["Journal", "/journal", "journal"], ["About", "/about"]] },
    { title: "Info", links: [["Contact", "/contact"], ["Privacy Policy", "/legal/privacy"], ["Cookie Policy", "/legal/cookies"], ["Terms", "/legal/terms"]] },
  ].map((col) => ({ ...col, links: col.links.filter(([, , feature]) => !feature || isEnabled(feature)) }));

  return (
    <footer className="bg-brand-surface border-t border-white/5 relative z-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={IMAGES.logo} alt="The Fish House Project" className="h-16 w-16 object-contain" />
            <span className="font-display font-extrabold text-sm tracking-tight leading-none">
              THE FISH HOUSE<br /><span className="text-brand-cyan">PROJECT</span>
            </span>
          </div>
          <p className="text-brand-textSec text-sm max-w-xs leading-relaxed">
            Building a tropical fish business from the ground up — with you.
          </p>
          <div className="flex items-center gap-3 mt-6">
            {socials.map(({ icon: Icon, key, label }) => {
              const href = social(key, settings);
              return (
                <a
                  key={key}
                  href={href || "#"}
                  onClick={(e) => !href && e.preventDefault()}
                  target={href ? "_blank" : undefined}
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className={`h-10 w-10 grid place-items-center rounded-full border border-white/10 transition-colors ${
                    href ? "text-brand-silver hover:text-brand-cyan hover:border-brand-cyan/40" : "text-brand-textMuted cursor-default"
                  }`}
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        </div>

        {cols.map((col) => (
          <div key={col.title}>
            <h4 className="text-white font-display font-semibold text-sm mb-4">{col.title}</h4>
            <ul className="space-y-2.5">
              {col.links.map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-brand-textSec hover:text-brand-cyan text-sm transition-colors"
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-brand-textMuted text-xs">© {new Date().getFullYear()} The Fish House Project. A journey in progress.</p>
          <p className="text-brand-textMuted text-xs">Built with you, not just for you.</p>
        </div>
      </div>
    </footer>
  );
};
